<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { gsap } from 'gsap';
import { quickSearchSections, searchSections } from '../config/search.js';
import { buildSearchQuery, normalizeSearchQuery } from '../composables/useSearchQuery.js';
import { searchResources } from '../services/searchService.js';

const route = useRoute();
const router = useRouter();
const dialog = ref(null);
const input = ref(null);
const query = ref('');
const section = ref('');
const preview = ref([]);
const previewState = ref('idle');
let trigger = null;
let previewTimer;
let requestController;

const focusableSelector = 'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

async function openSearch(event) {
  trigger = event?.target?.closest?.('[data-open-search]') || document.activeElement;
  const current = route.name === 'search' ? normalizeSearchQuery(route.query) : { q:'',section:'' };
  query.value = current.q;
  section.value = current.section;
  if (!dialog.value?.open) dialog.value?.showModal();
  document.body.classList.add('search-overlay-open');
  await nextTick();
  input.value?.focus();
  input.value?.select();
  if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
    gsap.fromTo(dialog.value.querySelector('.global-search-panel'),
      { clipPath:'inset(0 0 100% 0 round 28px)', y:-12 },
      { clipPath:'inset(0 0 0% 0 round 28px)', y:0, duration:.55, ease:'power3.out', clearProps:'clipPath,transform' });
    gsap.fromTo(dialog.value.querySelector('.global-search-form'),
      { scaleX:.95, autoAlpha:.3 },
      { scaleX:1, autoAlpha:1, duration:.45, delay:.12, ease:'power3.out', clearProps:'transform,opacity,visibility' });
  }
}

function closeSearch({ restoreFocus=true } = {}) {
  requestController?.abort();
  clearTimeout(previewTimer);
  if (dialog.value?.open) dialog.value.close();
  document.body.classList.remove('search-overlay-open');
  if (restoreFocus) nextTick(() => trigger?.focus?.());
}

function submitSearch() {
  closeSearch({ restoreFocus:false });
  router.push({ name:'search', query:buildSearchQuery({ q:query.value,section:section.value,page:1 }) });
}

function selectQuick(id) {
  section.value = section.value === id ? '' : id;
  input.value?.focus();
}

function onDocumentClick(event) {
  if (event.target.closest?.('[data-open-search]')) openSearch(event);
}

function onKeydown(event) {
  if (!dialog.value?.open) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    closeSearch();
    return;
  }
  if (event.key !== 'Tab') return;
  const controls = [...dialog.value.querySelectorAll(focusableSelector)].filter(element => element.offsetParent !== null);
  if (!controls.length) return;
  const first = controls[0];
  const last = controls.at(-1);
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
}

watch([query,section], () => {
  clearTimeout(previewTimer);
  requestController?.abort();
  const value = query.value.trim();
  if (value.length < 2) { preview.value=[]; previewState.value='idle'; return; }
  previewState.value='loading';
  previewTimer = setTimeout(async () => {
    requestController = new AbortController();
    try {
      const response = await searchResources({ query:value,section:section.value,pageSize:4,signal:requestController.signal });
      preview.value=response.items;
      previewState.value='success';
    } catch (error) {
      if (error.name !== 'AbortError') previewState.value='error';
    }
  },180);
});

watch(() => route.fullPath, () => closeSearch({ restoreFocus:false }));
onMounted(() => { document.addEventListener('click',onDocumentClick); document.addEventListener('keydown',onKeydown); });
onBeforeUnmount(() => { closeSearch({ restoreFocus:false }); document.removeEventListener('click',onDocumentClick); document.removeEventListener('keydown',onKeydown); });
</script>

<template>
  <dialog ref="dialog" class="global-search" aria-modal="true" aria-labelledby="global-search-title" @cancel.prevent="closeSearch()" @click.self="closeSearch()">
    <div class="global-search-panel">
      <button class="global-search-close" type="button" aria-label="إغلاق البحث" @click="closeSearch()"><i class="bi bi-x-lg"></i></button>
      <div class="global-search-inner">
        <span class="global-search-eyebrow">البحث في الدليل</span>
        <h2 id="global-search-title">ما الذي تبحث عنه؟</h2>
        <form class="global-search-form" role="search" @submit.prevent="submitSearch">
          <label class="visually-hidden" for="globalSearchInput">ابحث داخل الدليل</label>
          <span class="global-search-input"><i class="bi bi-search"></i><input id="globalSearchInput" ref="input" v-model="query" type="search" placeholder="ابحث داخل الدليل..." autocomplete="off"></span>
          <label class="visually-hidden" for="globalSearchSection">القسم</label>
          <select id="globalSearchSection" v-model="section"><option value="">جميع الأقسام</option><option v-for="item in searchSections" :key="item.id" :value="item.id">{{ item.fullLabel }}</option></select>
          <button type="submit">بحث</button>
        </form>
        <div class="global-search-quick" aria-label="أقسام سريعة"><span>بحث سريع:</span><button v-for="item in quickSearchSections" :key="item.id" type="button" :class="{active:section===item.id}" @click="selectQuick(item.id)"><i :class="`bi ${item.icon}`"></i>{{ item.label }}</button></div>
        <div v-if="previewState !== 'idle'" class="global-search-preview" aria-live="polite">
          <div v-if="previewState === 'loading'" class="global-preview-loading"><i v-for="index in 3" :key="index"></i></div>
          <p v-else-if="previewState === 'error'">تعذر تحميل النتائج المقترحة.</p>
          <template v-else>
            <RouterLink v-for="item in preview" :key="item.id" :to="item.path" @click="closeSearch({restoreFocus:false})"><span>{{ item.typeLabel }}</span><b>{{ item.title }}</b><i class="bi bi-arrow-left"></i></RouterLink>
            <button v-if="preview.length" class="global-search-all" type="button" @click="submitSearch">عرض جميع النتائج</button>
            <p v-else>لا توجد نتائج مطابقة لبحثك.</p>
          </template>
        </div>
      </div>
    </div>
  </dialog>
</template>
